import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader';
import PageTransition from '../../../components/common/PageTransition';
import { mockImportPreviewItems } from '../../../mocks/reactionsMockData';
// Types imported from '../../../types/reactions' for reference

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  VALID: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Valid' },
  WARNING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Warning' },
  INVALID: { bg: 'bg-red-100', text: 'text-red-800', label: 'Invalid' },
  SKIPPED: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Skipped' },
  PENDING: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pending' },
  IMPORTED: { bg: 'bg-emerald-500', text: 'text-white', label: 'Imported' },
};

const ImportPreviewPage: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number>(mockImportPreviewItems[0]?.item_id ?? 0);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [dedupeMode, setDedupeMode] = useState<string>('skip');

  const items = mockImportPreviewItems;
  const selected = items.find((i) => i.item_id === selectedId) ?? null;
  const validCount = items.filter((i) => i.status === 'VALID').length;

  const toggleSelect = (id: number) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <PageTransition>
      <div className="flex flex-col h-full min-h-screen" style={{ background: 'var(--content-bg)' }}>
        <PageHeader
          title="Import Preview"
          subtitle="Review parsed reactions before importing"
          backTo="/chemistry/reactions"
          backLabel="Reactions"
          breadcrumbs={[
            { label: 'Chemistry', to: '/chemistry/files' },
            { label: 'Reactions', to: '/chemistry/reactions' },
            { label: 'Import Preview' },
          ]}
        />

        {/* Split pane */}
        <div className="flex-1 flex px-6 pb-6 gap-4 max-w-7xl mx-auto w-full pt-4" style={{ minHeight: 0 }}>
          {/* Left – item list (40%) */}
          <div className="w-2/5 flex flex-col bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                Parsed Items ({items.length})
              </span>
              <div className="flex gap-2 text-xs text-gray-500">
                <span className="text-emerald-600 font-semibold">{validCount} valid</span>
                <span className="text-red-500 font-semibold">{items.filter((i) => i.status === 'INVALID').length} invalid</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.map((item) => {
                const sc = statusConfig[item.status] || statusConfig.PENDING;
                const sig = item.preview.reactants || item.preview.products
                  ? `${item.preview.reactants} \u2192 ${item.preview.products}`
                  : '—';
                const isActive = item.item_id === selectedId;
                return (
                  <div
                    key={item.item_id}
                    onClick={() => setSelectedId(item.item_id)}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 transition-colors ${
                      isActive ? 'bg-emerald-50 border-l-2 border-l-emerald-500' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.item_id)}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(item.item_id); }}
                      className="accent-emerald-500 w-4 h-4 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-400 font-mono w-6 text-right flex-shrink-0">
                      {item.source_row}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${sc.bg} ${sc.text} flex-shrink-0`}>
                      {sc.label}
                    </span>
                    <span className="text-sm text-gray-700 truncate flex-1">{sig}</span>
                    {item.preview.yieldPercent != null && (
                      <span className="text-xs font-semibold text-emerald-600 flex-shrink-0">
                        {item.preview.yieldPercent}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right – detail (60%) */}
          <div className="w-3/5 flex flex-col bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {selected ? (
              <>
                {/* Reaction viewer placeholder */}
                <div className="h-40 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border-b border-gray-100">
                  <code className="text-xs text-gray-500 max-w-lg text-center break-all px-4">
                    {selected.reaction_smiles || 'No SMILES'}
                  </code>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {/* Status */}
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${statusConfig[selected.status]?.bg} ${statusConfig[selected.status]?.text}`}>
                      {statusConfig[selected.status]?.label}
                    </span>
                    <span className="text-sm text-gray-500">Row {selected.source_row}</span>
                  </div>

                  {/* Parsed fields */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <FieldItem label="Reactants" value={String(selected.preview.reactants)} />
                    <FieldItem label="Products" value={String(selected.preview.products)} />
                    <FieldItem label="Catalysts" value={String(selected.preview.catalysts)} />
                    <FieldItem label="Atom mapping" value={selected.preview.hasAtomMapping ? 'Yes' : 'No'} />
                    <FieldItem label="Yield" value={selected.preview.yieldPercent != null ? `${selected.preview.yieldPercent}%` : '—'} />
                    <FieldItem label="Solvents" value={selected.preview.solvents?.join(', ') || '—'} />
                    <FieldItem label="Temp" value={selected.preview.tempC != null ? `${selected.preview.tempC} °C` : '—'} />
                    <FieldItem label="Time" value={selected.preview.timeH != null ? `${selected.preview.timeH} h` : '—'} />
                  </div>

                  {/* Errors */}
                  {selected.errors.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">Errors</h3>
                      <ul className="space-y-1">
                        {selected.errors.map((e, i) => (
                          <li key={i} className="text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {selected.warnings.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold text-yellow-600 uppercase tracking-wider mb-1">Warnings</h3>
                      <ul className="space-y-1">
                        {selected.warnings.map((w, i) => (
                          <li key={i} className="text-sm text-yellow-700 bg-yellow-50 px-3 py-1.5 rounded-lg">{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                Select an item from the list
              </div>
            )}
          </div>
        </div>

        {/* Bottom action bar */}
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/chemistry/reactions" className="text-sm text-gray-500 hover:text-gray-700">
              Cancel
            </Link>
            <div className="flex items-center gap-3">
              <select
                value={dedupeMode}
                onChange={(e) => setDedupeMode(e.target.value)}
                className="px-3 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-emerald-500"
              >
                <option value="skip">Duplicates: Skip</option>
                <option value="update">Duplicates: Update</option>
                <option value="allow">Duplicates: Allow</option>
              </select>
              <button
                disabled={selectedItems.size === 0}
                className="px-5 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Import Selected ({selectedItems.size})
              </button>
              <button className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                Import All Valid ({validCount})
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

const FieldItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="text-xs text-gray-400">{label}</span>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

export default ImportPreviewPage;
